import { useState, useEffect, useCallback } from 'react';
import { workflowApi } from '@/api/workflowApi';
import { reviewApi } from '@/api/reviewApi';
import type { WorkflowStatusResponse } from '@/types/workflow';
import type { ReviewGateDto } from '@/types/review';

export const useWorkflow = (opportunityId?: string) => {
  const [workflowStatus, setWorkflowStatus] = useState<WorkflowStatusResponse | null>(null);
  const [reviewGates, setReviewGates] = useState<ReviewGateDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchWorkflow = useCallback(async () => {
    if (!opportunityId) return;
    setLoading(true);
    setError(null);
    try {
      let status: WorkflowStatusResponse;
      try {
        status = await workflowApi.getWorkflowStatus(opportunityId);
      } catch (err: any) {
        // Workflow not yet initialized — try to initialize
        if (err.response?.status === 500 || err.response?.status === 404) {
          status = await workflowApi.initializeWorkflow(opportunityId);
        } else {
          throw err;
        }
      }
      setWorkflowStatus(status);

      // Always try to fetch review gates — they should be visible
      // once initialized (Phase E and beyond) for ownership management
      try {
        const gates = await reviewApi.getGates(opportunityId);
        setReviewGates(gates);
      } catch {
        // Review gates may not be initialized yet — that's OK
        setReviewGates([]);
      }
    } catch (err: any) {
      console.error('Failed to fetch workflow:', err);
      setError(err.message || 'Failed to fetch workflow');
    } finally {
      setLoading(false);
    }
  }, [opportunityId]);

  useEffect(() => {
    fetchWorkflow();
  }, [fetchWorkflow]);

  const advancePhase = async (reason: string) => {
    if (!opportunityId) return;
    const updated = await workflowApi.advanceWorkflow(opportunityId, reason);
    setWorkflowStatus(updated);
    // Re-fetch review gates (they may have been initialized on phase advance)
    try {
      const gates = await reviewApi.getGates(opportunityId);
      setReviewGates(gates);
    } catch {
      setReviewGates([]);
    }
    return updated;
  };

  const holdWorkflow = async () => {
    if (!opportunityId) return;
    const updated = await workflowApi.holdWorkflow(opportunityId);
    setWorkflowStatus(updated);
    return updated;
  };

  const cancelWorkflow = async () => {
    if (!opportunityId) return;
    const updated = await workflowApi.cancelWorkflow(opportunityId);
    setWorkflowStatus(updated);
    return updated;
  };

  return {
    workflowStatus,
    reviewGates,
    loading,
    error,
    refresh: fetchWorkflow,
    advancePhase,
    holdWorkflow,
    cancelWorkflow,
  };
};
